import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

/**
 * 1C balance reconciliation — comparing a client's balances in 1C against ours
 * and resolving the disagreement row by row.
 *
 * This is NOT the importer (`AdminService.import1CBalances`, `POST
 * /admin/1c/balances/:userId`). That route skips any currency the client
 * already has a Balance row for — existence-based, so a row sitting at zero
 * suppresses it forever — which makes it a one-shot opening-balance tool and
 * leaves it unable to express the case that matters here: both sides hold a
 * number and the two disagree.
 *
 * Two things drive the whole UI:
 *  - **A row you do not answer is left alone.** `resolutions` carries an
 *    explicit decision per 1C line (`lineId`) and an omitted row is skipped, never
 *    defaulted. Adopting 1C's figure for a row nobody looked at is the exact
 *    failure this screen exists to prevent, so the payload is built from what
 *    the operator actually touched — never padded to look uniform.
 *  - **Every money field is a decimal string.** Compare them with the
 *    `decimal.helper` functions or a decimal library; `parseFloat` is how a
 *    panel ends up reporting "no difference" on a real gap.
 */

/** `preview` is the only state an apply is accepted from. */
export type ReconciliationStatus =
  | 'preview'
  /** In flight — or a crashed process, which `abandon` clears. */
  | 'applying'
  | 'completed'
  /** Some rows failed. Expected, not a crash — see DRIFTED. */
  | 'completed_with_errors'
  | 'failed';

export type ReconciliationRowType = 'cash' | 'securities';

/**
 * Which side reported the row. Decides the safe default, and `ours_only` is the
 * one that can destroy a position — see RESOLUTION_DEFAULTS.
 */
export type ReconciliationPresence =
  | 'both'
  | 'one_c_only'
  | 'ours_only'
  /**
   * A 1C line whose ISIN matches several instruments, none picked
   * automatically. `currency` is null until the operator picks a candidate.
   */
  | 'unmapped'
  /** A 1C line with no instrument or malformed data. Can only be skipped. */
  | 'unresolved';

export type ReconciliationRowStatus =
  | 'pending'
  | 'applied'
  /**
   * Quantity unchanged, only the average price was set. No ledger row is
   * written because nothing moved, but it IS a success and is counted in
   * `totals.applied` — render it as one.
   */
  | 'cost_basis_only'
  | 'skipped'
  | 'failed';

export type ReconciliationResolution =
  | 'use_1c'
  | 'keep_ours'
  | 'custom'
  /** Recorded as `skipped-by-admin`. Carries nothing else — no amount, price or instrument. */
  | 'skip';

/** Refuses the whole run. `canRun` is false whenever any is present. */
export type ReconciliationBlockerCode =
  | 'NO_BROKERAGE_ACCOUNT'
  | 'KYC_NOT_APPROVED'
  | 'ONE_C_FETCH_FAILED'
  | 'PAYLOAD_ACCOUNT_MISMATCH'
  | 'RUN_IN_PROGRESS'
  // --- row level, after apply ------------------------------------------------
  | 'OVERSELL'
  | 'DELTA_NOT_REPRESENTABLE'
  | 'DRIFTED';

/** Never blocks. Several of these change what the operator must be shown. */
export type ReconciliationWarningCode =
  | 'PRIOR_IMPORT_EXISTS'
  | 'EMPTY_ONE_C_PAYLOAD'
  /** Run level: 1C reported ISINs with no instrument here. */
  | 'INSTRUMENTS_NOT_FOUND'
  /** Run and row level: several instruments share an ISIN and none was picked. */
  | 'INSTRUMENT_CHOICE_REQUIRED'
  /** Run level: 1C reported more than one line for a balance. */
  | 'DUPLICATE_ONE_C_LINES'
  /** Row level: this line shares its balance with another 1C line. */
  | 'DUPLICATE_ONE_C_LINE'
  /** Row level: no instrument carries this line's ISIN. */
  | 'INSTRUMENT_NOT_FOUND'
  /** Row level: 1C sent a line that could not be read. */
  | 'INVALID_DATA'
  | 'UNKNOWN_CURRENCY'
  | 'SETTLEMENT_ALIASED'
  | 'PRECISION_LOSS_SETTLEMENT'
  | 'PRECISION_LOSS_SECURITIES'
  | 'PRECISION_LOSS_CASH'
  | 'ZERO_DELTA_AFTER_PRECISION'
  | 'ONE_C_COST_BASIS_MISSING'
  | 'COST_BASIS_FROM_MARKET_QUOTE'
  /** A basis was asked for on a zero position — skipped, a basis on nothing is meaningless. */
  | 'COST_BASIS_NO_POSITION';

export type ReconciliationNoticeCode =
  | ReconciliationBlockerCode
  | ReconciliationWarningCode;

export interface ReconciliationNotice {
  code: ReconciliationNoticeCode;
  /** English-only prose. Branch on `code`, never on this. */
  message?: string;
  [key: string]: unknown;
}

export interface ReconciliationTotals {
  rows: number;
  conflicts: number;
  applied: number;
  skipped: number;
  failed: number;
}

/** An instrument carrying a securities line's ISIN, with our balance in it. */
export interface ReconciliationCandidate {
  instrumentInternalId: string;
  displayTicker: string | null;
  sourceId: string | null;
  status: string | null;
  /** Whether we hold a balance row for this instrument. */
  held: boolean;
  ourAmount: string | null;
  ourFrozen: string | null;
  ourAverageCostPrice: string | number | null;
  priorImportAmount: string | null;
  priorImportAt: string | null;
}

export interface ReconciliationRow {
  /**
   * `1c:cash:<n>`, `1c:sec:<n>` or `ours:<currency>`. The key `resolutions`
   * uses and the key `data.errors` points back at — unlike `currency`, it is
   * unique per run.
   */
  lineId: string;
  type: ReconciliationRowType;
  /**
   * Our balance key: `USD` for cash, `AAPL~NSDQ` for a holding. Repeats when
   * several 1C lines map to one balance, and is `null` on an `unmapped` line.
   * After apply it holds the instrument the decision was applied to.
   */
  currency: string | null;
  /** Securities only, as 1C sent it. */
  isin: string | null;
  /**
   * The currency 1C booked the line in. It is what tells two lines of one ISIN
   * apart (270 units in AMD, 15 in USD), so it is always shown.
   */
  oneCCurrency: string | null;
  presence: ReconciliationPresence;
  /** Securities: `auto` at preview, `admin` once the operator picked another candidate. */
  mapping: 'auto' | 'admin' | null;
  /** Securities: every instrument carrying this ISIN. */
  candidates: ReconciliationCandidate[];
  /** True on `unmapped` lines: pick a candidate, or skip. */
  requiresInstrumentChoice: boolean;
  /** The decisions the row accepts. `['skip']` on `unresolved` lines. */
  allowedResolutions: ReconciliationResolution[];
  /**
   * Set when 1C reported a minor unit — `GBX` pence against a `GBP` balance.
   * The API has already rescaled the key and the amount 100:1, so `oneCAmount`
   * is in POUNDS while this still says GBX. Show both or an operator checking
   * against 1C's own statement reads the figure as wrong by two orders of
   * magnitude.
   */
  sourceCurrency: string | null;
  scale: number;

  /**
   * What 1C says, as a decimal string. **`null` means 1C did not mention the
   * holding at all** — which is emphatically not the same as 1C reporting zero,
   * and the two must not render alike. A real 1C zero is an instruction to
   * close the position and arrives as `presence: 'both'` with `"0.0000"`.
   */
  oneCAmount: string | null;
  ourAmount: string | null;
  /**
   * `oneCAmount − ourAmount`, precomputed. Positive when 1C is ahead. `null`
   * on an `unmapped` line — there is no "ours" until an instrument is chosen.
   */
  difference: string | null;
  /** Open orders / pending withdrawals. Securities rows only. */
  ourFrozen: string | null;

  /** What earlier 1C syncs contributed, and when. */
  priorImportAmount: string | null;
  priorImportAt: string | null;

  /**
   * Securities only. 1C sends the position's TOTAL cost in `AveragePrice`; the
   * API has already divided it by the quantity, so this is a genuine per-unit
   * price and must be labelled as one.
   */
  oneCAverageCostPrice: string | number | null;
  ourAverageCostPrice: string | number | null;

  status: ReconciliationRowStatus;
  /** Populated after an apply — `keep-ours`, `skipped-by-admin`, `drifted: was X, now Y`, … */
  reason: string | null;
  /** Failure codes after apply — OVERSELL, DRIFTED, … */
  blockers: ReconciliationNotice[];
  warnings: ReconciliationNotice[];
  /** False ⇒ `skip` is the only allowed decision. */
  resolvable: boolean;
}

/** The persisted run record. `preview` creates one; `apply` returns it terminal. */
export interface ReconciliationRun {
  id: string;
  userId: string;
  brokerageAccount: string | null;
  status: ReconciliationStatus;
  /** The business day 1C's numbers are as of — not when they were fetched. */
  oneCDate: string | null;
  fetchedAt: string;
  totals: ReconciliationTotals;
  blockers: ReconciliationNotice[];
  warnings: ReconciliationNotice[];
  /** False whenever `blockers` is non-empty; apply enforces the same list. */
  canRun: boolean;
  /** Omitted by the list endpoint — fetch the run by id for per-row detail. */
  rows?: ReconciliationRow[];
  adminId?: string;
  startedAt?: string;
  finishedAt?: string;
}

export interface ReconciliationDecision {
  /**
   * Must match a row's `lineId`. Duplicates are rejected, and so is a decision
   * keyed by the old `currency`.
   */
  lineId: string;
  resolution: ReconciliationResolution;
  /**
   * One of the line's `candidates`. Required on an `unmapped` line; optional
   * otherwise. Must be omitted with `skip`.
   */
  instrumentInternalId?: string;
  /**
   * The TARGET balance, not a delta — the backend computes the delta against a
   * live read at apply time. Required for `custom`, and must be OMITTED for
   * `use_1c`, `keep_ours` and `skip`; sending it anyway is ERROR_VALIDATION.
   */
  amount?: number;
  /**
   * Per-unit price. Securities only, optional on every resolution, and `0` is
   * rejected. Wins over 1C's.
   *
   * It is also how the average price is changed WITHOUT moving the quantity:
   * `keep_ours` + `costPrice`, or `custom` with the amount already held +
   * `costPrice`, comes back `cost_basis_only`. Omitting it on `custom` or
   * `keep_ours` means "leave the basis alone"; a price equal to the current
   * basis is skipped.
   */
  costPrice?: number;
}

export interface ApplyReconciliationPayload {
  /**
   * Required. Without it the call is refused and nothing happens, so a
   * double-submitted or replayed request cannot move balances by accident.
   */
  confirm: true;
  /** Only the rows the operator decided. Omitted rows are skipped, not defaulted. */
  resolutions: ReconciliationDecision[];
  /** Recompute deltas against the live balance instead of failing DRIFTED rows. */
  allowDrift?: boolean;
}

export type ReconciliationDecisionErrorCode =
  | 'UNKNOWN_LINE'
  | 'LINE_NOT_RESOLVABLE'
  | 'INSTRUMENT_CHOICE_REQUIRED'
  | 'INSTRUMENT_NOT_A_CANDIDATE'
  | 'DUPLICATE_TARGET';

/**
 * One entry of `data.errors` on an `ERROR_VALIDATION` whose `data.code` is
 * `INVALID_DECISIONS`. Every invalid line is reported at once, and the run
 * stays in `preview` so the payload can be fixed and resent.
 */
export interface ReconciliationDecisionError {
  lineId: string;
  code: ReconciliationDecisionErrorCode | string;
  /** DUPLICATE_TARGET: the balance both lines point at. */
  currency?: string;
  /** DUPLICATE_TARGET: the line already targeting it. */
  otherLineId?: string;
  [key: string]: unknown;
}

export interface ReconciliationListParams {
  page: number;
  itemsPerPage: number;
  userId?: string;
  /** `any|preview|applying|completed|completed_with_errors|failed`. */
  status?: string;
}

export interface ReconciliationListResponse {
  items: ReconciliationRun[];
  total: number;
}

/**
 * A preview older than this is refused with PREVIEW_EXPIRED. 1C's numbers go
 * stale too and the preview is the operator's only view of them, so the fix is
 * always to re-preview — never to retry the apply.
 */
export const PREVIEW_TTL_MS = 30 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class OneCBalanceReconciliationService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  /** Already ends in `/api`, so these resolve to `/api/admin/1c/…`. */
  private readonly hostUrl: string;

  constructor() {
    this.hostUrl = this.env.host;
  }

  /**
   * Reads both sides and PERSISTS a run record to apply against. No balance
   * moves, but it is not free of side effects — each call creates a run.
   */
  preview(userId: string): Observable<ReconciliationRun> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/1c/balances/${encodeURIComponent(userId)}/preview`,
      {}
    );
  }

  /** Only ever call this from a preview the backend said `canRun`. */
  apply(
    id: string,
    payload: ApplyReconciliationPayload
  ): Observable<ReconciliationRun> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/1c/reconciliations/${encodeURIComponent(id)}/apply`,
      payload
    );
  }

  /** Rows are omitted here — use {@link getById} for the per-row detail. */
  list(params: ReconciliationListParams): Observable<ReconciliationListResponse> {
    const query: Record<string, string | number> = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
    };
    if (params.userId) query['userId'] = params.userId;
    if (params.status) query['status'] = params.status;
    return this.networkHelper.get(`${this.hostUrl}/admin/1c/reconciliations`, {
      ...query,
    });
  }

  getById(id: string): Observable<ReconciliationRun> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/1c/reconciliations/${encodeURIComponent(id)}`
    );
  }

  /**
   * Clears a run stuck in `applying` after a crashed process so a fresh preview
   * can proceed. Admin session only, and only valid on an `applying` run.
   */
  abandon(id: string): Observable<ReconciliationRun> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/1c/reconciliations/${encodeURIComponent(id)}/abandon`,
      {}
    );
  }
}
